import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
    buyCoin, 
    getCoinHistory, 
    setAutoPayment, 
    getAutoPayment,
    getCoinProductList
} from '../../api/services/payment/coin';
import type { 
    BuyCoinRequest, 
    CoinHistoryRequest, 
    AutoPaymentRequest 
} from '../../api/types/payment';

// Query Keys
export const COIN_QUERY_KEYS = {
    history: ['coin', 'history'],
    autoPayment: ['coin', 'autoPayment'],
    productList: ['coin', 'productList'],
} as const;

export const useCoin = () => {
    const queryClient = useQueryClient();

    // 코인 구매 mutation
    const { mutate: purchaseCoin, isPending: isPurchasing } = useMutation({
        mutationFn: (request: BuyCoinRequest) => buyCoin(request),
        onSuccess: () => {
            // 구매 성공 시 코인 내역 쿼리 무효화
            queryClient.invalidateQueries({ queryKey: COIN_QUERY_KEYS.history });
        },
    });

    // 코인 내역 조회 query
    const useCoinHistory = (request: CoinHistoryRequest) => {
        return useQuery({
            queryKey: [...COIN_QUERY_KEYS.history, request],
            queryFn: () => getCoinHistory(request),
        });
    };

    // 자동 결제 설정 mutation
    const { mutate: updateAutoPayment, isPending: isUpdatingAutoPayment } = useMutation({
        mutationFn: (request: AutoPaymentRequest) => setAutoPayment(request),
        onSuccess: () => {
            // 설정 변경 시 자동 결제 설정 쿼리 무효화
            queryClient.invalidateQueries({ queryKey: COIN_QUERY_KEYS.autoPayment });
        },
    });

    // 자동 결제 설정 조회 query
    const { data: autoPaymentSettings, isLoading: isLoadingAutoPayment } = useQuery({
        queryKey: COIN_QUERY_KEYS.autoPayment,
        queryFn: () => getAutoPayment(),
    });

    return {
        // Mutations
        purchaseCoin,
        isPurchasing,
        updateAutoPayment,
        isUpdatingAutoPayment,
        
        // Queries
        useCoinHistory,
        autoPaymentSettings,
        isLoadingAutoPayment,
    };
};

// 코인 제품 리스트 조회 훅
export const useCoinProductList = () => {
    return useQuery({
        queryKey: COIN_QUERY_KEYS.productList,
        queryFn: getCoinProductList,
        staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
    });
}; 